// app/src/pages/RecurrentBookings.tsx
import React, { useEffect, useMemo, useState } from "react";
import api from "../api/Client";
import { SecureStorage } from "../storage/SecureStorage";
import { APRENDE_PRODUCT_ID, createAprendeTask, extractBookingId } from "../utils/aprendeTasks";

/* ==== Tipos ==== */
type ServiceItem = { service_id: number; service_name: string; centro?: string | null };
type TimeslotOption = { id: number; name: string };
type WeeklyHour = { service_id: number; date: string; hour: string; booked: number; capacity: number; duration?: number };
type HourlyCoach = { service_id: number; date: string; hour: string; coach_id: number; coach_name: string; booked: number; capacity: number };

type UserProduct = {
  id: number;
  name: string;
  serviceIds: number[];
  sessionsRemaining?: number | null;
  centro?: string | null;
};

type RecurrentBookingResult = {
  date: string;
  time: string;
  ok: boolean;
  msg?: string;
};

/* ==== Utils (idénticos en espíritu a CalendarPage) ==== */
const parseYMD = (s: string) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, (m || 1) - 1, (d || 1)); };
const ymd = (d: Date) => { const x = new Date(d.getTime()); x.setHours(12, 0, 0, 0); return x.toISOString().slice(0, 10); };
function mondayOf(dateStr: string) { const d = parseYMD(dateStr); const dow = (d.getDay() + 6) % 7; d.setDate(d.getDate() - dow); return ymd(d); }
function addDays(d: Date, n: number) { const x = new Date(d.getTime()); x.setDate(x.getDate() + n); return x; }

const resultDateFormatter = new Intl.DateTimeFormat("es-ES", {
  weekday: "short",
  day: "2-digit",
  month: "short",
});

function getRequestErrorMessage(error: any, fallback = "Error al crear la reserva") {
  return String(
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    fallback
  ).trim();
}

function normalizeFailureMessage(message: string) {
  const normalized = String(message || "").trim();
  const lower = normalized.toLowerCase();

  if (!normalized) return "Error al crear la reserva";
  if (lower.includes("no quedan sesiones")) return "No quedan sesiones disponibles";
  if (lower.includes("capacidad del coach alcanzada") || lower.includes("aforo completo")) return "La sesión está llena";
  if (lower.includes("no hay comodines") || lower.includes("recuperacion")) return normalized;

  return normalized;
}

function formatResultDate(dateStr: string) {
  return resultDateFormatter.format(parseYMD(dateStr));
}

const semanasMax = 4; // máximo de semanas a repetir (Poner 12 cuando se implemente VIP)

/* =================== Componente =================== */
export default function RecurrentBookings() {
  const today = new Date();

  // Estado base
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isVip, setIsVip] = useState<boolean>(false);

  // Datos
  const [products, setProducts] = useState<UserProduct[]>([]);
  const [services, setServices] = useState<ServiceItem[]>([]);
  const [timeslots, setTimeslots] = useState<TimeslotOption[]>([]);

  // Selección
  const [selectedDate, setSelectedDate] = useState<Date>(today);
  const [selectedProductId, setSelectedProductId] = useState<number | "">("");
  const [selectedServiceId, setSelectedServiceId] = useState<number | "">("");
  const [selectedHours, setSelectedHours] = useState<Record<string, string>>({}); // { 'YYYY-MM-DD': 'HH:mm' }

  // Horas del día y detalle de hora
  const [dayHours, setDayHours] = useState<WeeklyHour[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState<string | null>(null);

  const [hourDetail, setHourDetail] = useState<{
    hour: string;
    loading: boolean;
    error: string | null;
    coaches: HourlyCoach[];
    chosenCoachId: number | null;
  }>({ hour: "", loading: false, error: null, coaches: [], chosenCoachId: null });

  // Número de semanas
  const [weeksRepeat, setWeeksRepeat] = useState<number>(4);
  const [creatingBookings, setCreatingBookings] = useState(false);
  const [bookingResults, setBookingResults] = useState<RecurrentBookingResult[]>([]);
  const [resultsModalOpen, setResultsModalOpen] = useState(false);

  /* ==== Carga inicial ==== */
  useEffect(() => {
    (async () => {
      setLoading(true); setError(null);
      try {
        const user = await SecureStorage.getUser();
        if (!user?.id) throw new Error("Usuario no disponible. Inicia sesión de nuevo.");

        // VIP Quitar comentario en version en la que se meta logica VIP
        /*try {
          const rVip = await api.get(`/api/customers/is-vip/${user.id}`);
          const { isVip } = (rVip.data || {}) as { isVip?: boolean };
          setIsVip(!!isVip);
        } catch { setIsVip(false); }*/

        // Datos base
        const [rServices, rTimeslots, rUserProducts] = await Promise.all([
          api.get(`/api/set-up/services`),
          api.get(`/api/set-up/session-timeslots`),
          api.get(`/api/mobile/user-products`, { params: { user_id: user.id } }),
        ]);

        const servicesArr: ServiceItem[] = Array.isArray(rServices.data) ? rServices.data : [];
        setServices(servicesArr);

        const ts = ((Array.isArray(rTimeslots.data) ? rTimeslots.data : []) as any[]).map(t => ({
          id: t.session_timeslot_id,
          name: t.timeslot,
        }));
        setTimeslots(ts);

        const normalizedProducts: UserProduct[] = (Array.isArray(rUserProducts.data) ? rUserProducts.data : []).map((p: any) => ({
          id: Number(p.id ?? p.product_id ?? 0),
          name: String(p.name ?? p.product_name ?? ""),
          serviceIds: Array.isArray(p.serviceIds) ? p.serviceIds.map(Number) :
                      String(p.service_ids || "").split(",").map((x: string) => Number(x.trim())).filter(Boolean),
          sessionsRemaining: p.sessionsRemaining ?? null,
          centro: (p.centro ?? p.center ?? null),
        }));
        setProducts(normalizedProducts);
      } catch (e: any) {
        setError(e.message || "No se pudieron cargar los datos.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
  if (!selectedServiceId) return;
  fetchWeeklyForDay(Number(selectedServiceId), ymd(selectedDate));
  // al cambiar de día, resetea el detalle/elección de coach
  setHourDetail({ hour: "", loading: false, error: null, coaches: [], chosenCoachId: null });
  }, [selectedDate, selectedServiceId]);


  // Producto seleccionado y servicios filtrados por centro salvo VIP
  const selectedProduct = useMemo(
    () => (selectedProductId ? products.find(p => p.id === selectedProductId) || null : null),
    [selectedProductId, products]
  );
  const allowedServiceIds: number[] = useMemo(() => selectedProduct?.serviceIds ?? [], [selectedProduct]);

  const servicesForProduct = useMemo(() => {
    if (!selectedProductId || !allowedServiceIds.length) return [];
    return services.filter(s => {
      const matchesProduct = allowedServiceIds.includes(Number(s.service_id));
      if (!matchesProduct) return false;
      if ((selectedProduct?.centro || "").toLowerCase() === "vip") return true;
      if (selectedProduct?.centro) {
        return (s.centro || "").toLowerCase() === (selectedProduct.centro || "").toLowerCase();
      }
      return true;
    });
  }, [selectedProductId, allowedServiceIds, services, selectedProduct?.centro]);

  // Autoseleccionar servicio único
  useEffect(() => {
    if (!selectedProductId) { setSelectedServiceId(""); return; }
    if (servicesForProduct.length === 1) setSelectedServiceId(servicesForProduct[0].service_id);
    else if (!servicesForProduct.find(s => s.service_id === selectedServiceId)) setSelectedServiceId("");
  }, [selectedProductId, servicesForProduct, selectedServiceId]);

  /* ==== Cargar horas del día ==== */
  async function fetchWeeklyForDay(serviceId: number, dateStr: string) {
    try {
      setSlotsLoading(true); setSlotsError(null);
      const r = await api.get(`/api/booking/weekly`, {
        params: { service_id: serviceId, date: dateStr, single_day: 1 }
      });
      const all: WeeklyHour[] = Array.isArray(r.data) ? r.data : [];
      const filtered = all
        .filter(x => x.date === dateStr)
        .map(x => ({ ...x, free: Math.max(0, (x.capacity ?? 0) - (x.booked ?? 0)) }))
        .filter((x: any) => x.free > 0)
        .sort((a, b) => a.hour.localeCompare(b.hour));
      setDayHours(filtered);
    } catch (e: any) {
      setSlotsError(e.message || "Error al consultar disponibilidad");
    } finally {
      setSlotsLoading(false);
    }
  }

  /* ==== Detalle de hora (coaches) ==== */
  async function loadHourDetail(hourHHMMSS: string) {
    if (!selectedServiceId) return;
    const hhmm = hourHHMMSS.slice(0, 5);
    setHourDetail({ hour: hhmm, loading: true, error: null, coaches: [], chosenCoachId: null });
    try {
      const r = await api.get(`/api/booking/hourly`, {
        params: { service_id: selectedServiceId, date: ymd(selectedDate), time: hhmm }
      });
      const data: HourlyCoach[] = Array.isArray(r.data) ? r.data : [];
      const withFree = data.filter(c => (c.capacity ?? 0) - (c.booked ?? 0) > 0);
      const chosenId = withFree[0]?.coach_id ?? null;
      setHourDetail({ hour: hhmm, loading: false, error: null, coaches: withFree, chosenCoachId: chosenId });
    } catch (e: any) {
      setHourDetail({ hour: "", loading: false, error: e.message || "Error al cargar detalle", coaches: [], chosenCoachId: null });
    }
  }

  function getTimeslotIdForHour(hhmm: string) {
    const f = timeslots.find(t => (t.name || "").slice(0, 5) === hhmm);
    return f ? f.id : null;
  }

  /* ==== Semana (L→D) de la fecha seleccionada ==== */
  const weekDays = useMemo(() => {
    const base = new Date(selectedDate);
    const day = base.getDay(); // 0 dom, 1 lun
    const diffToMonday = (day === 0 ? -6 : 1 - day);
    const monday = new Date(base); monday.setDate(base.getDate() + diffToMonday);
    return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
  }, [selectedDate]);

  /* ==== Crear reservas recurrentes ==== */
  async function createRecurrentBookings() {
    if (!selectedProductId) return alert("Selecciona un producto.");
    if (!selectedServiceId) return alert("Selecciona un servicio.");
    if (!Object.keys(selectedHours).length) return alert("Selecciona al menos una hora en algún día.");

    const user = await SecureStorage.getUser();
    if (!user?.id) return alert("Usuario no disponible.");
    const userName = String(user?.fullName ?? user?.name ?? user?.email ?? "").trim();
    const isAprendeProduct = Number(selectedProductId) === APRENDE_PRODUCT_ID;
    setCreatingBookings(true);

    const results: RecurrentBookingResult[] = [];

    try {
      for (let i = 0; i < weeksRepeat; i++) {
        for (const [dateStr, hhmm] of Object.entries(selectedHours)) {
          const dateObj = parseYMD(dateStr);
          dateObj.setDate(dateObj.getDate() + i * 7);
          const finalDateStr = ymd(dateObj);

          const session_timeslot_id = getTimeslotIdForHour(hhmm);
          if (!session_timeslot_id) {
            results.push({
              date: finalDateStr,
              time: hhmm,
              ok: false,
              msg: "No se encontró el horario seleccionado"
            });
            continue;
          }

          try {
            const r = await api.get(`/api/booking/hourly`, {
              params: { service_id: selectedServiceId, date: finalDateStr, time: hhmm }
            });
            const data: HourlyCoach[] = Array.isArray(r.data) ? r.data : [];
            const freeCoach = data.find(c => (c.capacity ?? 0) - (c.booked ?? 0) > 0);
            if (!freeCoach) {
              results.push({
                date: finalDateStr,
                time: hhmm,
                ok: false,
                msg: "La sesión está llena"
              });
              continue;
            }

            const payload = {
              product_id: selectedProductId,
              customer_id: user.id,
              coach_id: freeCoach.coach_id,
              service_id: selectedServiceId,
              session_timeslot_id,
              start_date: finalDateStr,
              amount: 0, discount: 0, total_amount: 0,
              coupon_id: null, payment_method: "cash",
              bank_account_id: null, card_id: null,
            };
            if (Number(payload.coupon_id) > 0) {
              (payload as any).recover = 1;
            }

            const res = await api.post(`/api/coaches/create-booking`, payload);
            const ok = !!((res.data as any)?.success || (res.data as any)?.booking_id);
            if (!ok) {
              throw new Error((res.data as any)?.error || (res.data as any)?.message || "Error al crear la reserva");
            }

            let successMessage: string | undefined;
            if (isAprendeProduct) {
              const bookingId = extractBookingId(res.data);
              try {
                await createAprendeTask({
                  bookingId,
                  coachId: freeCoach.coach_id,
                  startDateYmd: finalDateStr,
                  userName,
                });
              } catch (taskError: any) {
                successMessage = getRequestErrorMessage(taskError, "Reserva creada, pero no se pudo generar la tarea Aprende");
              }
            }

            results.push({
              date: finalDateStr,
              time: hhmm,
              ok: true,
              msg: successMessage
            });
          } catch (e: any) {
            results.push({
              date: finalDateStr,
              time: hhmm,
              ok: false,
              msg: normalizeFailureMessage(getRequestErrorMessage(e))
            });
          }
        }
      }
    } finally {
      const sortedResults = results.slice().sort((a, b) => {
        const left = `${a.date} ${a.time}`;
        const right = `${b.date} ${b.time}`;
        return left.localeCompare(right);
      });
      setBookingResults(sortedResults);
      setResultsModalOpen(true);
      setCreatingBookings(false);
    }
  }

  /* =================== Render =================== */
  if (loading) return <div style={{ padding: 16 }}>Cargando…</div>;
  if (error) return <div style={{ padding: 16, color: "crimson" }}>{error}</div>;
  // Quitar comentario en version en la que se meta logica VIP
  /*if (!isVip) {
    return (
      <div style={pageOuter}>
        <div style={contentWrap}>
          <div style={card}>
            <h2 style={h2}>Reservas recurrentes</h2>
            <p style={muted}>Esta sección es exclusiva para usuarios VIP.</p>
          </div>
        </div>
      </div>
    );
  }*/

  const selectedYmd = ymd(selectedDate);

  return (
    <div style={pageOuter}>
      <div style={pageHeader}>
        <span style={headerTitle}>Reservas Recurrentes</span>
      </div>

      <div style={contentWrap}>
        {/* Producto */}
        <section style={card}>
          <h4 style={sectionTitle}>Producto</h4>
          <select
            value={selectedProductId ?? ""}
            onChange={(e) => {
              const pid = Number(e.target.value) || null;
              setSelectedProductId((pid || "") as any);
              setSelectedServiceId("");
              setDayHours([]);
              setHourDetail({ hour: "", loading: false, error: null, coaches: [], chosenCoachId: null });
              setSelectedHours({});
            }}
            style={pillSelect}
          >
            <option value="">{products.length ? "Selecciona un producto…" : "No tienes productos"}</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          {selectedProduct?.centro && (
            <div style={helperText}>Centro del producto: <strong>{String(selectedProduct.centro).toUpperCase()}</strong></div>
          )}
        </section>

        {/* Servicio */}
        <section style={card}>
          <h4 style={sectionTitle}>Servicio</h4>
          <select
            value={selectedServiceId ?? ""}
            onChange={(e) => {
              const sid = Number(e.target.value) || 0;
              setSelectedServiceId((sid || "") as any);
              setDayHours([]);
              if (sid) fetchWeeklyForDay(sid, selectedYmd);
            }}
            style={pillSelect}
            disabled={!selectedProductId}
            title={!selectedProductId ? "Elige primero un producto" : ""}
          >
            <option value="">{selectedProductId ? "Selecciona un servicio…" : "Elige primero un producto"}</option>
            {servicesForProduct.map((s) => (
              <option key={s.service_id} value={s.service_id}>{s.service_name}</option>
            ))}
          </select>
        </section>

        {/* Semana (L→D) */}
        <section style={card}>
          <h4 style={sectionTitle}>Semana</h4>
          <div style={weekRow}>
            {weekDays.map((d) => {
              const iso = ymd(d);
              const active = iso === ymd(selectedDate);
              return (
                <button
                  key={iso}
                  onClick={() => setSelectedDate(d)}
                  style={{ ...weekBtn, ...(active ? weekBtnActive : {}) }}
                  aria-label={`Día ${new Intl.DateTimeFormat("es-ES", { weekday: "long", day: "2-digit" }).format(d)}`}
                >
                  <div style={{ fontWeight: 800 }}>
                    {new Intl.DateTimeFormat("es-ES", { weekday: "short" }).format(d)}
                  </div>
                  <div>{d.getDate()}</div>
                </button>
              );
            })}
          </div>
        </section>

        {/* Horas disponibles */}
        <section style={card}>
          <h4 style={sectionTitle}>Horas disponibles</h4>
          {!selectedServiceId ? (
            <p style={muted}>Selecciona primero un servicio.</p>
          ) : slotsLoading ? (
            <p>Cargando horas…</p>
          ) : slotsError ? (
            <p style={{ color: "#b91c1c" }}>{slotsError}</p>
          ) : (!dayHours || dayHours.length === 0) ? (
            <p style={muted}>No hay horas para el día seleccionado.</p>
          ) : (
            <div style={hoursGrid}>
              {dayHours.map((slot) => {
                const hourHHMM = slot.hour.slice(0, 5);
                const iso = ymd(selectedDate);
                const isActive = selectedHours[iso] === hourHHMM;
                return (
                  <button
                    key={`${slot.service_id}-${slot.date}-${slot.hour}`}
                    onClick={() => {
                      setSelectedHours((prev) => ({ ...prev, [iso]: hourHHMM }));
                      loadHourDetail(slot.hour);
                    }}
                    style={{ ...timeBtn, ...(isActive ? timeBtnActive : {}) }}
                  >
                    {hourHHMM}
                  </button>
                );
              })}
            </div>
          )}

          {/* Detalle de hora / elección de coach */}
          {hourDetail.hour && !hourDetail.loading && !hourDetail.error && hourDetail.coaches.length > 0 && (
            <div style={coachBlock}>
              <div style={coachBlockTitle}>profesional disponible para las {hourDetail.hour}h</div>
              <select
                value={hourDetail.chosenCoachId ?? ""}
                onChange={(e) =>
                  setHourDetail(h => ({ ...h, chosenCoachId: e.target.value ? Number(e.target.value) : null }))
                }
                style={pillSelect}
              >
                {hourDetail.coaches.map(c => (
                  <option key={c.coach_id} value={c.coach_id}>{c.coach_name}</option>
                ))}
              </select>
            </div>
          )}
        </section>

        {/* Repetición (semanas) */}
        <section style={card}>
          <h4 style={sectionTitle}>Repetición</h4>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <label style={{ fontWeight: 700, color: "#374151" }}>Repetir durante</label>
            <input
              type="number"
              min={1}
              max={semanasMax}
              value={weeksRepeat}
              onChange={(e) => setWeeksRepeat(Math.max(1, Math.min(semanasMax, Number(e.target.value || 1))))}
              style={numInput}
            />
            <span style={muted}>semanas</span>
          </div>
          <p style={{ ...muted, marginTop: 8 }}>
            Se crearán reservas en los mismos días/horas seleccionados, desplazados semanalmente.
          </p>
        </section>

        {/* Resumen de selección */}
        {Object.keys(selectedHours).length > 0 && (
          <section style={card}>
            <h4 style={sectionTitle}>Resumen</h4>
            <div style={chipsWrap}>
              {Object.entries(selectedHours).sort(([a], [b]) => a.localeCompare(b)).map(([date, hhmm]) => (
                <span key={date} style={chip}>
                  {new Intl.DateTimeFormat("es-ES", { weekday: "short", day: "2-digit", month: "short" }).format(parseYMD(date))} · {hhmm}
                </span>
              ))}
            </div>
            {/* Boton reserva */}
            <div style={{ marginTop: 12 }}>
              <button
                onClick={createRecurrentBookings}
                disabled={
                  creatingBookings ||
                  !selectedProductId ||
                  !selectedServiceId ||
                  !Object.keys(selectedHours).length ||
                  !hourDetail.chosenCoachId
                }
                style={{
                  ...reserveCta,
                  ...( creatingBookings ||
                    !selectedProductId ||
                    !selectedServiceId ||
                    !Object.keys(selectedHours).length ||
                    !hourDetail.chosenCoachId
                      ? reserveCtaDisabled : {} ),
                }}
              >
                {creatingBookings ? "Creando reservas..." : `Crear reservas (${weeksRepeat} sem.)`}
              </button>
            </div>
          </section>
        )}
        <div style={{ height: 96 }} />
      </div>

      {resultsModalOpen && (
        <div style={resultModalOverlay} role="dialog" aria-modal="true" aria-labelledby="recurrent-results-title">
          <div style={resultModalCard}>
            <div style={resultModalHeader}>
              <div>
                <h3 id="recurrent-results-title" style={resultModalTitle}>Resultado de las reservas</h3>
                <p style={resultModalSubtitle}>
                  {bookingResults.filter(item => item.ok).length} realizadas · {bookingResults.filter(item => !item.ok).length} no realizadas
                </p>
              </div>
              <button type="button" onClick={() => setResultsModalOpen(false)} style={resultModalCloseBtn} aria-label="Cerrar">
                ×
              </button>
            </div>

            <div style={resultList}>
              {bookingResults.length ? bookingResults.map((item, index) => (
                <div
                  key={`${item.date}-${item.time}-${index}`}
                  style={{
                    ...resultItemCard,
                    ...(item.ok ? resultItemCardOk : resultItemCardFail)
                  }}
                >
                  <div style={resultItemMain}>
                    <div style={resultItemDate}>{formatResultDate(item.date)} · {item.time}</div>
                    <div style={resultItemStatusText}>{item.ok ? "Reserva realizada" : "Reserva no realizada"}</div>
                    {!item.ok && (
                      <div style={resultItemReason}>{item.msg || "Error al crear la reserva"}</div>
                    )}
                    {item.ok && item.msg && (
                      <div style={resultItemReason}>{item.msg}</div>
                    )}
                  </div>
                  <span style={{ ...resultStatusBadge, ...(item.ok ? resultStatusBadgeOk : resultStatusBadgeFail) }}>
                    {item.ok ? "OK" : "Error"}
                  </span>
                </div>
              )) : (
                <div style={resultEmptyState}>No se pudo procesar ninguna reserva.</div>
              )}
            </div>

            <button type="button" onClick={() => setResultsModalOpen(false)} style={resultModalActionBtn}>
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* =================== Estilos (alineados al resto de la app) =================== */
const pageOuter: React.CSSProperties = { position: "relative", minHeight: "100dvh", background: "#f3f4f6" };
const pageHeader: React.CSSProperties = { padding: "12px 12px 0" };
const headerTitle: React.CSSProperties = { fontWeight: 800, fontSize: 18, color: "#111827" };

const contentWrap: React.CSSProperties = { padding: 12, display: "grid", gap: 12, maxWidth: 760, margin: "0 auto" };
const card: React.CSSProperties = { background: "#fff", borderRadius: 12, padding: 14, border: "1px solid #e5e7eb", boxShadow: "0 1px 2px rgba(0,0,0,.04)" };

const h2: React.CSSProperties = { margin: 0, fontSize: 18, fontWeight: 800, color: "#111827" };
const sectionTitle: React.CSSProperties = { margin: "0 0 8px 0", fontWeight: 800, color: "#111827" };
const muted: React.CSSProperties = { color: "#6b7280", fontSize: 13 };
const helperText: React.CSSProperties = { marginTop: 6, fontSize: 12, color: "#6b7280" };

const pillSelect: React.CSSProperties = {
  width: "100%", padding: "10px 12px", borderRadius: 999, border: "1px solid #e5e7eb",
  background: "#fff", color: "#111827", fontWeight: 600
};

const weekRow: React.CSSProperties = { display: "flex", justifyContent: "center", gap: 10, flexWrap: "wrap" };
const weekBtn: React.CSSProperties = {
  padding: "10px 14px", borderRadius: 10, border: "1px solid #e5e7eb", background: "#fff",
  color: "#111827", cursor: "pointer", minWidth: 84, display: "grid", placeItems: "center"
};
const weekBtnActive: React.CSSProperties = { background: "#1976d2", color: "#fff", borderColor: "#1976d2" };

const hoursGrid: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(84px, 1fr))", gap: 10 };
const timeBtn: React.CSSProperties = {
  padding: "10px 12px", borderRadius: 10, background: "#fff", border: "1px solid #e5e7eb",
  fontWeight: 700, cursor: "pointer"
};
const timeBtnActive: React.CSSProperties = { background: "#dbeafe", borderColor: "#93c5fd", color: "#1d4ed8" };

const coachBlock: React.CSSProperties = { marginTop: 12, display: "grid", gap: 8 };
const coachBlockTitle: React.CSSProperties = { fontWeight: 800, color: "#111827" };

const numInput: React.CSSProperties = {
  width: 80, padding: "8px 10px", borderRadius: 10, border: "1px solid #e5e7eb", background: "#fff", fontWeight: 700
};

const chipsWrap: React.CSSProperties = { display: "flex", flexWrap: "wrap", gap: 8 };
const chip: React.CSSProperties = {
  padding: "6px 10px", borderRadius: 999, background: "#e3f2fd", color: "#1d4ed8",
  border: "1px solid #cfe8ff", fontWeight: 700, fontSize: 12
};
const reserveCta: React.CSSProperties = {
  width: "100%", padding: "16px 14px", border: "none", borderRadius: 12, fontWeight: 800, fontSize: 16,
  background: "#1976d2", color: "#fff", cursor: "pointer"
};
const reserveCtaDisabled: React.CSSProperties = { opacity: 0.5, cursor: "not-allowed" };

const resultModalOverlay: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(17, 24, 39, 0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 16,
  zIndex: 50
};
const resultModalCard: React.CSSProperties = {
  width: "100%",
  maxWidth: 560,
  maxHeight: "88vh",
  overflow: "hidden",
  background: "#fff",
  borderRadius: 18,
  boxShadow: "0 20px 50px rgba(15, 23, 42, 0.2)",
  display: "grid",
  gap: 16,
  padding: 18
};
const resultModalHeader: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 12
};
const resultModalTitle: React.CSSProperties = {
  margin: 0,
  fontSize: 18,
  fontWeight: 800,
  color: "#111827"
};
const resultModalSubtitle: React.CSSProperties = {
  margin: "4px 0 0",
  color: "#6b7280",
  fontSize: 13
};
const resultModalCloseBtn: React.CSSProperties = {
  border: "none",
  background: "#f3f4f6",
  color: "#374151",
  borderRadius: 10,
  width: 36,
  height: 36,
  fontSize: 24,
  lineHeight: 1,
  cursor: "pointer"
};
const resultList: React.CSSProperties = {
  display: "grid",
  gap: 10,
  overflowY: "auto",
  paddingRight: 2
};
const resultItemCard: React.CSSProperties = {
  borderRadius: 14,
  border: "1px solid #e5e7eb",
  padding: 12,
  display: "flex",
  justifyContent: "space-between",
  gap: 12,
  alignItems: "flex-start"
};
const resultItemCardOk: React.CSSProperties = {
  background: "#f0fdf4",
  borderColor: "#bbf7d0"
};
const resultItemCardFail: React.CSSProperties = {
  background: "#fef2f2",
  borderColor: "#fecaca"
};
const resultItemMain: React.CSSProperties = {
  display: "grid",
  gap: 4
};
const resultItemDate: React.CSSProperties = {
  color: "#111827",
  fontWeight: 800,
  textTransform: "capitalize"
};
const resultItemStatusText: React.CSSProperties = {
  color: "#374151",
  fontSize: 13,
  fontWeight: 700
};
const resultItemReason: React.CSSProperties = {
  color: "#6b7280",
  fontSize: 13,
  lineHeight: 1.4
};
const resultStatusBadge: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 62,
  padding: "6px 10px",
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 800
};
const resultStatusBadgeOk: React.CSSProperties = {
  background: "#dcfce7",
  color: "#166534"
};
const resultStatusBadgeFail: React.CSSProperties = {
  background: "#fee2e2",
  color: "#b91c1c"
};
const resultEmptyState: React.CSSProperties = {
  borderRadius: 14,
  background: "#f9fafb",
  border: "1px solid #e5e7eb",
  padding: 16,
  textAlign: "center",
  color: "#6b7280",
  fontSize: 14
};
const resultModalActionBtn: React.CSSProperties = {
  width: "100%",
  border: "none",
  borderRadius: 12,
  background: "#111827",
  color: "#fff",
  padding: "14px 16px",
  fontWeight: 800,
  cursor: "pointer"
};
